/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  combineReducers,
  composeReducers,
  composeSetters,
  fieldValidationSetter,
  forceUpdatedDirtyType,
  removeDirtyRow,
  setDirty,
  setEntityField,
  setEntityFields,
  setNewDirtyType,
  setRecalculationNeeded,
  setUpdatedDirtyType,
  Table,
  whenRemoved,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { constant, difference, flatMap } from 'lodash';
import {
  createJeTop,
  getJeComponentId,
  JeTop,
  validateJeTopField,
} from '../../../resources/job-estimate-details.model';
import {
  jeCcTable,
  jeComponentTable,
  JeDetailsState,
  JeDetailsStateEvent,
  JeDetailsStateEventType,
  jeItemTable,
  jeTopTable,
  queryJeComponentItems,
  queryJeTopRefItem,
} from '../je-details.state';

const setTopField = composeSetters(setEntityField, setUpdatedDirtyType, fieldValidationSetter(validateJeTopField));

export const jeDetailsTopReducer = composeReducers(
  combineReducers({
    jobTops: topsReducer,
  }),
  topDirtyFlow,
  processChangesAsapReducer,
  rootTopReducer,
);

function topsReducer(tops: Table<JeTop>, event: JeDetailsStateEvent): Table<JeTop> {
  switch (event.type) {
    case JeDetailsStateEventType.TopSetField:
      return jeTopTable.mergeById(tops, event.topId, (row: JeTop) => setTopField(row, event.name, event.value));
    case JeDetailsStateEventType.TopCommit:
      // reset isFromGto flag if top was changed
      return jeTopTable.mergeById(
        tops,
        event.topId,
        (top: JeTop) => top.isFromGto ? setEntityField(top, 'isFromGto', false) : top);
    case JeDetailsStateEventType.TopReset:
      return jeTopTable.mergeById(tops, event.top.topId, constant(event.top));
    case JeDetailsStateEventType.TopRemove:
      return jeTopTable.mergeById(tops, event.topId, (top) => removeDirtyRow(setEntityField(top, 'isFromGto', false)));
    default:
      return tops;
  }
}

function topDirtyFlow(state: JeDetailsState, event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.TopCommit: {
      const components = jeComponentTable.byGroup(state.jobComponents, 'topId', event.topId);
      const componentItems = flatMap(
        components,
        (component) => jeItemTable.byGroup(state.jobItems, 'component', getJeComponentId(component)));
      const freeItems = jeItemTable.byGroup(state.jobItems, 'topId', event.topId);
      const allItems = freeItems.concat(componentItems);
      const affectedCcIds = allItems
        .map((item) => item.costCenterId)
        .concat(components.length ? [state.jobComponentsCcId] : [])
        .concat(flatMap(
          components,
          (component) => queryJeComponentItems(state, getJeComponentId(component)).map((item) => item.costCenterId)));
      return setEntityFields(state, {
        jobComponents: jeComponentTable.updateAll(state.jobComponents, components.map(setUpdatedDirtyType)),
        jobItems: jeItemTable.updateAll(state.jobItems, allItems.map(setUpdatedDirtyType)),
        jobCostCenters: jeCcTable.mergeAllById(state.jobCostCenters, affectedCcIds, setUpdatedDirtyType),
      });
    }
    default:
      return state;
  }
}

function processChangesAsapReducer(state: JeDetailsState, event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.TopCommit:
      return setEntityField(state, 'isProcessChangesAsap', true);
    default:
      return state;
  }
}

function rootTopReducer(state: JeDetailsState, event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.TopRemove:
      return setDirty(state);
    case JeDetailsStateEventType.TopCommit:
      return setRecalculationNeeded(setDirty(state));
    default:
      return state;
  }
}

export function addOrUpdateTops(state: JeDetailsState, topIds: ID[]): JeDetailsState {
  return setEntityField(
    state,
    'jobTops',
    jeTopTable.mergeAllById(
      state.jobTops,
      topIds,
      (row, id) => {
        if (row) {
          if (whenRemoved(row)) {
            return forceUpdatedDirtyType(row);
          } else {
            return setUpdatedDirtyType(row);
          }
        } else {
          const top = queryJeTopRefItem(state, id);

          if (top) {
            return setNewDirtyType(createJeTop(top));
          }
        }
      }));
}

export function getModifiedTops(sourceTopIds: ID[], targetTopIds: ID[]): ID[] {
  return difference(sourceTopIds, targetTopIds).concat(difference(targetTopIds, sourceTopIds));
}
